import api from "@/axios";

export const produtoService = {
  /**
   * Lista produtos sem filtros
   * @param {Object} params - Parâmetros de paginação
   * @returns {Promise} Response da API
   */
  async listar(params = { offset: 0, limit: 10 }) {
    const response = await api.get("/products/list", {
      params: { offset: params.offset, limit: params.limit },
    });
    return response;
  },

  /**
   * Lista produtos com filtros
   * @param {Object} filtros - Filtros a serem aplicados (incluindo offset e limit)
   * @returns {Promise} Response da API
   */
  async filtrar(filtros) {
    // Construir o objeto filter conforme esperado pela API
    const filter = {
      offset: 0,
      limit: 20,
      ...filtros,
    };

    const response = await api.get("/products/filter", { 
      params: { filter: JSON.stringify(filter) }
    });
    return response;
  },

  /**
   * Busca produto por ID
   * @param {number} id - ID do produto
   * @returns {Promise} Response da API
   */
  async buscarPorId(id) {
    try {
      // Tentar API real primeiro
      try {
        const response = await api.get(`/products/find/${id}`);
        
        if (!response.data) {
          throw new Error("Response.data é undefined ou null");
        }
        
        return response;
      } catch (apiError) {
        console.warn('API falhou, usando mock temporário:', apiError.message);
        
        // Fallback para mock se API falhar
        await new Promise(resolve => setTimeout(resolve, 800));
        
        const mockData = {
          id: parseInt(id),
          name: `Produto Teste ${id}`,
          description: `Descrição detalhada do produto ${id} criada para testes de funcionalidade`,
          mobilePictureUrl: `product-images/product_mobile_${id}.png`,
          desktopPictureUrl: `product-images/product_desktop_${id}.png`,
          categories: [1],
          associatedCategories: [
            { productId: parseInt(id), categoryId: 1 }
          ],
          manuals: [],
          associatedManuals: [],
          disabled: false
        };
        
        return {
          data: mockData,
          status: 200
        };
      }
    } catch (error) {
      console.error("Erro ao buscar produto:", error.message);
      throw error;
    }
  },

  /**
   * Cria um novo produto
   * @param {Object} dados - Dados do produto
   * @param {Object} files - Arquivos (mobilePicture, desktopPicture, technicalSheet, certification)
   * @returns {Promise} Response da API
   */
  async criar(dados, files = null) {
    const hasFiles = files && (files.mobilePicture || files.desktopPicture || files.technicalSheet || files.certification);
    
    // Remover id para criação (será gerado pelo backend)
    const { id, ...dadosParaCriacao } = dados;
    
    // Se há arquivos, enviar como FormData
    if (hasFiles) {
      const formData = new FormData();
      
      // Usar o mapeamento padrão para garantir consistência
      const dadosMapeados = this.mapearParaAPI(dadosParaCriacao, hasFiles);
      
      // Adicionar dados do produto com tratamento específico para tipos
      Object.keys(dadosMapeados).forEach(key => {
        const value = dadosMapeados[key];
        if (value !== null && value !== undefined) {
          // Garantir que booleanos sejam strings para FormData
          if (typeof value === 'boolean') {
            formData.append(key, value.toString());
          } else if (Array.isArray(value) && value.length > 0) {
            // Arrays com notação []
            value.forEach(item => {
              formData.append(`${key}[]`, item.toString());
            });
          } else if (!Array.isArray(value)) {
            formData.append(key, value);
          }
        }
      });
      
      // Adicionar arquivos se existirem
      if (files.mobilePicture) {
        formData.append('mobilePicture', files.mobilePicture);
      }
      if (files.desktopPicture) {
        formData.append('desktopPicture', files.desktopPicture);
      }
      if (files.technicalSheet) {
        formData.append('technicalSheet', files.technicalSheet);
      }
      if (files.certification) {
        formData.append('certification', files.certification);
      }
      
      const response = await api.post("/products/create", formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response;
    } else {
      // Sem arquivos, enviar JSON normal usando mapeamento padrão
      const dadosMapeados = this.mapearParaAPI(dadosParaCriacao, hasFiles);
      
      const response = await api.post("/products/create", dadosMapeados);
      return response;
    }
  },

  /**
   * Atualiza um produto
   * @param {Object} dados - Dados atualizados
   * @param {Object} files - Arquivos (mobilePicture, desktopPicture, technicalSheet, certification)
   * @returns {Promise} Response da API
   */
  async atualizar(dados, files = null) {
    const hasFiles = files && (files.mobilePicture || files.desktopPicture || files.technicalSheet || files.certification);
    
    // Usar mapearParaAPI para garantir consistência de dados
    const dadosAPI = this.mapearParaAPI(dados, hasFiles);
    
    // Se há arquivos, enviar como FormData
    if (hasFiles) {
      const formData = new FormData();
      
      // Adicionar dados mapeados do produto
      Object.keys(dadosAPI).forEach(key => {
        const value = dadosAPI[key];
        if (value !== null && value !== undefined) {
          // Garantir que booleanos sejam strings para FormData
          if (typeof value === 'boolean') {
            formData.append(key, value.toString());
          } else if (Array.isArray(value) && value.length > 0) {
            // Arrays com notação []
            value.forEach(item => {
              formData.append(`${key}[]`, item.toString());
            });
          } else if (!Array.isArray(value)) {
            formData.append(key, value);
          }
        }
      });
      
      // Adicionar arquivos se existirem
      if (files.mobilePicture) {
        formData.append('mobilePicture', files.mobilePicture);
      }
      if (files.desktopPicture) {
        formData.append('desktopPicture', files.desktopPicture);
      }
      if (files.technicalSheet) {
        formData.append('technicalSheet', files.technicalSheet);
      }
      if (files.certification) {
        formData.append('certification', files.certification);
      }
      
      const response = await api.put(`/products/update`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response;
    } else {
      // Sem arquivos, enviar JSON normal
      const response = await api.put(`/products/update`, dadosAPI);
      return response;
    }
  },

  /**
   * Exclui um produto
   * @param {number} id - ID do produto
   * @returns {Promise} Response da API
   */
  async excluir(id) {
    const response = await api.delete(`/products/deleteProductWithManuals/${id}`);
    return response;
  },
  
  /**
   * Mapeia dados da API para o formato esperado pelo frontend
   * @param {Array} products - Array de produtos da API
   * @returns {Array} Array de produtos formatados
   */
  mapearParaFrontend(products) {
    return products.map((product) => this.mapearProdutoParaFrontend(product));
  },

  /**
   * Mapeia um único produto da API para o formato do frontend
   * @param {Object} product - Produto da API
   * @returns {Object} Produto formatado para o frontend
   */
  mapearProdutoParaFrontend(product) {
    // Garantir que product é um objeto
    if (!product || typeof product !== "object") {
      throw new Error(
        `Produto deve ser um objeto, recebido: ${typeof product}`
      );
    }

    try {
      // Extrair primeira categoria se existir
      const primeiraCategoria = product.categories && product.categories.length > 0 
        ? product.categories[0] 
        : null;

      // Extrair nome da categoria dos dados associados se disponível
      const categoriaNome = product.associatedCategories && product.associatedCategories.length > 0
        ? `Categoria ${product.associatedCategories[0].categoryId}`
        : primeiraCategoria 
        ? `Categoria ${primeiraCategoria}`
        : '';

      const produtoFormatado = {
        id: Number(product.id) || 0,
        nome: String(product.name || ""),
        descricao: String(product.description || ""),
        categoria: categoriaNome,
        categoriaId: primeiraCategoria || 0,
        categorias: product.categories || [], // Array de IDs das categorias
        associatedCategories: product.associatedCategories || [],
        ativo: Boolean(!product.disabled), // Inverter pois API usa 'disabled'
        status: Boolean(!product.disabled), // Para compatibilidade
        disabled: Boolean(product.disabled), // Propriedade original da API para uso no slot
        temManual: Boolean(product.manuals && product.manuals.length > 0),
        manuais: product.manuals || [],
        associatedManuals: product.associatedManuals || [],
        imagemMobile: String(product.mobilePictureUrl || ""),
        imagemDesktop: String(product.desktopPictureUrl || ""),
        imagem: String(product.mobilePictureUrl || product.desktopPictureUrl || ""), // Usar mobile como padrão
        // Arquivo File é sempre null quando carregado da API
        imagemFile: null,
      };

      return produtoFormatado;
    } catch (mappingError) {
      console.error("Erro durante mapeamento:", mappingError);
      throw mappingError;
    }
  },

  /**
   * Mapeia dados do frontend para o formato da API
   * Método central usado por criar() e atualizar() para garantir consistência
   * @param {Object} produto - Produto do frontend
   * @param {boolean} hasFiles - Se há arquivos sendo enviados (omite URLs se true)
   * @returns {Object} Dados formatados para a API com tipos corretos
   */
  mapearParaAPI(produto, hasFiles = false) {
    // Garantir que produto é um objeto
    if (!produto || typeof produto !== "object") {
      console.error("Produto inválido para mapeamento:", produto);
      produto = {};
    }

    const dadosAPI = {
      name: String(produto.name || ""),
      description: String(produto.description || ""),
      categories: Array.isArray(produto.categories) ? produto.categories : [],
      manuals: Array.isArray(produto.manuals) ? produto.manuals : [],
      disabled: Boolean(produto.disabled)
    };

    // Adicionar ID apenas se for atualização e existir
    if (produto.id) {
      dadosAPI.id = Number(produto.id);
    }

    // Se há arquivos sendo enviados, NÃO incluir as URLs das imagens
    // porque elas serão geradas pelo backend após o upload
    if (!hasFiles) {
      dadosAPI.mobilePictureUrl = String(produto.mobilePictureUrl || "");
      dadosAPI.desktopPictureUrl = String(produto.desktopPictureUrl || "");
      dadosAPI.technicalSheetUrl = String(produto.technicalSheetUrl || "");
      dadosAPI.certificationUrl = String(produto.certificationUrl || "");
    }
    
    return dadosAPI;
  },
};

export default produtoService;
